// Library Methods
import React from "react";
import Plot from "react-plotly.js";
import { useTranslation } from "react-i18next";

// Components
import { Box, Typography } from "@mui/material";

// TODO: remove placeholder data
const heatmapData = {
  x: [
    -5, -5, -4, -4, -4, -4, -3, -3, -3, -3, -3, -3, -2, -2, -2, -2, -2, -2, -2,
    -2, -1, -1, -1, -1, -1, -1, -1, -1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2,
    2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4,
  ],
  y: [
    -1, 1, -1, 1, -2, 2, 3, 1, 2, -1, -2, -3, 1, 2, 3, 4, -4, -1, -2, -3, -2,
    -3, -4, 4, 3, 2, -1, 1, 1, 3, 2, -1, -3, -4, -2, 4, -1, -3, -4, 1, 4, 3, 2,
    -2, -2, -3, 3, 2, 2, -2, 1, -1,
  ],
  z: [
    -6.0, -4.0, -12.0, -8.0, -8.0, -4.0, -2.0, -2.0, -4.0, -4.0, -6.0, -10.0,
    -14.0, -6.0, -2.0, 2.0, -8.0, -10.0, -8.0, -6.0, -10.0, -8.0, -6.0, -28.23,
    -6.0, -4.0, -12.0, -2.0, -8.0, -6.0, -6.0, -8.0, -12.0, -12.0, -6.0, -28.25,
    -12.0, -6.0, -6.0, -6.0, -2.0, -2.0, -4.0, -8.0, -6.0, -12.0, -4.0, -20.0,
    -6.0, -8.0, -6.0, -6.0,
  ],
};

// TODO: remove placeholder data
const heatmapDataRight = {
  x: heatmapData.x.map((val) => -val),
  y: [...heatmapData.y],
  z: [
    -5.0, -3.5, -11.0, -7.0, -9.0, -3.0, -1.0, -1.5, -5.0, -3.0, -7.0, -11.0,
    -13.0, -5.5, -3.0, 1.0, -7.0, -9.5, -7.5, -5.0, -11.0, -7.5, -5.5, -27.0,
    -5.0, -3.5, -11.0, -1.0, -9.0, -5.0, -7.0, -7.5, -13.0, -11.0, -5.5, -29.0,
    -13.0, -5.5, -7.0, -5.0, -3.0, -1.0, -5.0, -9.0, -5.5, -13.0, -3.5, -19.0,
    -7.0, -7.5, -7.0, -5.5,
  ],
};

const MAX_COLOR_VAL = 5;
const MIN_COLOR_VAL = -35;

const colorscale = [
  ["0.0", "#F44336"],
  ["0.75", "#F9D976"],
  ["1.0", "#4CAF50"],
];
const tickvals = [5, 0, -5, -10, -15, -20, -25, -30, -35];
const ticktext = [
  "  5  ",
  "  0  ",
  " -5  ",
  " -10  ",
  " -15  ",
  " -20  ",
  " -25  ",
  " -30  ",
  " -35  ",
];

const HeatmapChart = ({
  chartTitle,
  colorbarTitle,
  date,
  vrfi,
  isRightEye,
}) => {
  const { t } = useTranslation();

  // TODO: change data to be a component parameter
  const heatmapDataToUse = isRightEye ? heatmapDataRight : heatmapData;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        alignItems="center"
        marginRight="15%"
      >
        <Typography fontWeight={500}>{chartTitle}</Typography>
        <Box display="flex" gap="16px">
          <Typography fontSize="12px">
            {t("report_exam_date")}: {date}
          </Typography>
          <Typography fontSize="12px">VRFI: {vrfi}</Typography>
        </Box>
      </Box>
      <Plot
        data={[
          {
            x: heatmapDataToUse.x,
            y: heatmapDataToUse.y,
            z: heatmapDataToUse.z,
            text: heatmapDataToUse.z,
            texttemplate: "%{text:.0f}", // Display z values with 0 decimal places
            textfont: { size: 12, color: "#000" },
            type: "heatmap",
            zsmooth: "best",
            coloraxis: "coloraxis", // Reference the coloraxis defined in layout
          },
        ]}
        config={{ staticPlot: true, displayModeBar: false }}
        layout={{
          shapes: [
            // x axis line
            {
              type: "line",
              x0: isRightEye ? -5 : -6,
              x1: isRightEye ? 6 : 5,
              y0: 0,
              y1: 0,
              line: { color: "#000", width: 1 },
            },
            // y axis line
            {
              type: "line",
              x0: 0,
              x1: 0,
              y0: 5,
              y1: -5,
              line: { color: "#000", width: 1 },
            },
            // top right triangle
            {
              type: "path",
              path: isRightEye
                ? "M 2.2 4.49 L 5.49 4.49 L 5.49 1.2 Z"
                : "M 2.2 4.49 L 4.49 4.49 L 4.49 2.2 Z",
              fillcolor: "#FFF",
              line: {
                color: "#FFF",
              },
            },

            // bottom right triangle
            {
              type: "path",
              path: isRightEye
                ? "M 2.2 -4.49 L 5.49 -4.49 L 5.49 -1.2 Z"
                : "M 2.2 -4.49 L 4.49 -4.49 L 4.49 -2.2 Z",
              fillcolor: "#FFF",
              line: {
                color: "#FFF",
              },
            },
            // top left triangle
            {
              type: "path",
              path: isRightEye
                ? "M -2.2 4.49 L -4.49 4.49 L -4.49 2.2 Z"
                : "M -2.2 4.49 L -5.49 4.49 L -5.49 1.2 Z",
              fillcolor: "#FFF",
              line: {
                color: "#FFF",
              },
            },
            // bottom left triangle
            {
              type: "path",
              path: isRightEye
                ? "M -2.2 -4.49 L -4.49 -4.49 L -4.49 -2.2 Z"
                : "M -2.2 -4.49 L -5.49 -4.49 L -5.49 -1.2 Z",
              fillcolor: "#FFF",
              line: {
                color: "#FFF",
              },
            },
          ],
          xaxis: {
            visible: false,
          },
          yaxis: {
            visible: false,
          },
          plot_bgcolor: "#FFF",
          paper_bgcolor: "#FFF",
          coloraxis: {
            cmax: MAX_COLOR_VAL, // max value on the colorbar
            cmin: MIN_COLOR_VAL, // min value on the colorbar
            colorscale,
            colorbar: {
              title: {
                text: colorbarTitle,
                side: "right",
              },
              thickness: 20,
              ypad: 0,
              tickmode: "array",
              tickvals,
              ticktext,
            },
          },
          margin: {
            t: 10,
            l: 0,
            r: 0,
            b: 10,
          },
          autosize: true,
        }}
        useResizeHandler
        style={{
          minWidth: 450,
          minHeight: 300,
          maxWidth: 450,
          width: "100%",
          height: "100%",
        }}
      />
    </>
  );
};

export default HeatmapChart;
